import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { addDays } from 'date-fns';
import {
  API,
  enrollmentsQueryOptions,
  locationsQueryOptions,
  myWaitlistsQueryOptions,
  schoolQueryOptions,
  sessionQueryOptions,
  sessionsQueryOptions,
  skillLevelsQueryOptions,
  studentCreditsQueryOptions,
  studentSubscriptionsQueryOptions,
  useAuthStore,
  useOnlineStatus,
} from '@sportschool/shared-logic';
import { SessionStatus } from '@sportschool/shared-types';
import { getCoachPortalSessionsFrom, getStudentPortalSessionRange } from '@/lib/portalSessionRanges';

/** How often the prefetch runs while the app stays open. */
const PREFETCH_INTERVAL_MS = 15 * 60 * 1000;
/** Coach: sessions starting within this many days get their detail / enrollments / attendance cached. */
const COACH_DETAIL_DAYS = 7;
/** Safety cap on the number of sessions prefetched in detail per run. */
const COACH_DETAIL_MAX_SESSIONS = 40;

async function settle(task: Promise<unknown>) {
  try {
    await task;
  } catch {
    // Best effort: a failed prefetch only means less data offline.
  }
}

/** Same derivation as StudentPortalPage / StudentEnrollmentsPage (`enrolledSessionIds`). */
function uniqueSessionIds(enrollments: { sessionId: string }[]): string[] {
  return Array.from(new Set(enrollments.map((e) => e.sessionId)));
}

async function prefetchSchoolBasics(qc: QueryClient, schoolId: string) {
  await Promise.all([
    settle(qc.prefetchQuery(schoolQueryOptions(schoolId))),
    settle(qc.prefetchQuery(locationsQueryOptions(schoolId))),
  ]);
}

async function prefetchCoach(qc: QueryClient, schoolId: string, coachMemberId: string, isCancelled: () => boolean) {
  await prefetchSchoolBasics(qc, schoolId);
  await settle(qc.prefetchQuery(skillLevelsQueryOptions(schoolId)));

  // Same query as CoachPortalPage (key + params), so the portal list is served from the cache.
  const sessions = await qc
    .fetchQuery(sessionsQueryOptions(schoolId, { from: getCoachPortalSessionsFrom(), coachMemberId }))
    .catch(() => []);

  const now = Date.now();
  const horizon = addDays(new Date(now), COACH_DETAIL_DAYS).getTime();
  const upcoming = sessions
    .filter((s) => s.status === SessionStatus.Scheduled)
    .filter((s) => {
      const endsAt = new Date(s.endsAt).getTime();
      return endsAt >= now && new Date(s.startsAt).getTime() <= horizon;
    })
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())
    .slice(0, COACH_DETAIL_MAX_SESSIONS);

  // Sequential on purpose: keeps the request burst small.
  for (const session of upcoming) {
    if (isCancelled()) return;
    await Promise.all([
      settle(qc.prefetchQuery(sessionQueryOptions(schoolId, session.id))),
      settle(qc.prefetchQuery(enrollmentsQueryOptions(schoolId, session.id))),
      settle(qc.prefetchQuery(API.getGetAttendanceForSessionQueryOptions(schoolId, session.id))),
    ]);
  }
}

async function prefetchStudent(qc: QueryClient, schoolId: string, studentMemberId: string) {
  const range = getStudentPortalSessionRange();
  const [enrollments] = await Promise.all([
    // Portal: useEnrollments(schoolId) · Enrollments history page: useEnrollments(schoolId, undefined, false).
    qc.fetchQuery(enrollmentsQueryOptions(schoolId)).catch(() => []),
    settle(qc.prefetchQuery(enrollmentsQueryOptions(schoolId, undefined, false))),
    prefetchSchoolBasics(qc, schoolId),
    settle(qc.prefetchQuery(sessionsQueryOptions(schoolId, { ...range, forStudent: true }))),
    settle(qc.prefetchQuery(API.getListCoachesPublicQueryOptions(schoolId))),
    settle(qc.prefetchQuery(myWaitlistsQueryOptions(schoolId))),
    ...(studentMemberId
      ? [
          settle(qc.prefetchQuery(studentSubscriptionsQueryOptions(schoolId, studentMemberId))),
          settle(qc.prefetchQuery(studentCreditsQueryOptions(schoolId, studentMemberId))),
        ]
      : []),
  ]);

  // History sessions of the enrolled groups (second sessions query of both student pages).
  const enrolledSessionIds = uniqueSessionIds(enrollments);
  if (enrolledSessionIds.length > 0) {
    await settle(qc.prefetchQuery(sessionsQueryOptions(schoolId, { sessionId: enrolledSessionIds, forStudent: true })));
  }
}

/**
 * Warms the query cache (persisted to IndexedDB, see `queryClient.ts`) with what a coach / student needs to use the
 * app offline. Runs when online right after the school context is ready and every 15 minutes; never while offline.
 * `prefetchQuery` respects `staleTime`, so a run over fresh data costs no requests.
 *
 * Every query goes through the same option factories the pages use (`@sportschool/shared-logic`), so keys cannot drift.
 */
export function useOfflinePrefetch(schoolId: string, ready: boolean) {
  const queryClient = useQueryClient();
  const { browserOnline } = useOnlineStatus();
  const activeRole = useAuthStore((s) => s.activeRole);
  const memberId = useAuthStore((s) => s.userContext?.activeSchool?.memberId ?? '');

  useEffect(() => {
    if (!ready || !browserOnline || !schoolId || !memberId) return;
    if (activeRole !== 'Coach' && activeRole !== 'Student') return;

    let cancelled = false;
    let running = false;

    const run = async () => {
      if (running || cancelled) return;
      running = true;
      try {
        if (activeRole === 'Coach') {
          await prefetchCoach(queryClient, schoolId, memberId, () => cancelled);
        } else {
          await prefetchStudent(queryClient, schoolId, memberId);
        }
      } finally {
        running = false;
      }
    };

    void run();
    const timer = setInterval(() => void run(), PREFETCH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [ready, browserOnline, schoolId, memberId, activeRole, queryClient]);
}
